import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Inventory2Outlined as ContainerIcon,
} from '@mui/icons-material';
import EntityDetailRow from '../common/EntityDetailRow';
import { ContainerSchema, ContainerStatus } from '../../types/container.type';
import { CONTAINER_STATUS_COLORS, statusChipSx } from '../../constants/status-colors';

const STATUS_LABEL_KEYS: Record<ContainerStatus, string> = {
  [ContainerStatus.PENDING]: 'containers.status.pending',
  [ContainerStatus.EMPTY]: 'containers.status.empty',
  [ContainerStatus.AWAITING_PICKUP]: 'containers.status.awaitingPickup',
  [ContainerStatus.READY_FOR_PICKUP]: 'containers.status.readyForPickup',
  [ContainerStatus.MAINTENANCE]: 'containers.status.maintenance',
  [ContainerStatus.INACTIVE]: 'containers.status.inactive',
};

export interface ContainerDeleteDialogProps {
  open: boolean;
  container: ContainerSchema | null;
  collectionSiteName?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ContainerDeleteDialog({
  open,
  container,
  collectionSiteName,
  loading = false,
  onClose,
  onConfirm,
}: ContainerDeleteDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!container) return null;

  const displayName =
    container.data?.name ||
    t('containers.map.containerFallback', { id: container.id.slice(0, 8) });
  const locationLabel =
    container.data?.location_name?.trim() || t('containers.card.locationNotSet');
  const materialName = container.material_type?.name || t('common.notAvailable');
  const statusColor = CONTAINER_STATUS_COLORS[container.status] ?? theme.palette.grey[500];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.error.main, 0.14),
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          >
            <DeleteIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
              {t('containers.deleteDialog.caption')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, m: 0 }}>
              {t('containers.deleteDialog.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
              {displayName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('containers.deleteDialog.warningTitle')}
          </Typography>
          <Typography variant="body2">{t('containers.deleteDialog.warningDetail')}</Typography>
        </Alert>

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          {t('containers.deleteDialog.detailsTitle')}
        </Typography>

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <ContainerIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
              {displayName}
            </Typography>
            <Chip
              size="small"
              label={t(STATUS_LABEL_KEYS[container.status])}
              sx={statusChipSx(statusColor)}
            />
          </Box>

          <Box sx={{ px: 2, py: 1 }}>
            <EntityDetailRow label={t('containers.form.deviceId')}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {container.device_id}
              </Typography>
            </EntityDetailRow>
            <EntityDetailRow label={t('containers.form.materialType')}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {materialName}
              </Typography>
            </EntityDetailRow>
            <EntityDetailRow label={t('containers.card.location')}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {locationLabel}
              </Typography>
            </EntityDetailRow>
            {collectionSiteName ? (
              <EntityDetailRow label={t('containers.form.collectionSite')}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {collectionSiteName}
                </Typography>
              </EntityDetailRow>
            ) : null}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('containers.deleteDialog.description')}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          sx={{ textTransform: 'none', px: 2.5 }}
        >
          {loading ? t('containers.deleteDialog.deleting') : t('containers.deleteDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
